import React from 'react';

import ListingHeader from 'components/common/ListingHeader/ListingHeader';
import ListingSidebar from 'components/common/ListingSidebar/ListingSidebar';
import {
  Breadcrumbs, Error, Image, Skeleton
} from 'components/ui';
import { useNavigationDispatchHelpers } from 'context/NavigationContext';
import * as BreadcrumbsHelpers from 'helpers/BreadcrumbsHelpers';
import { useCategory } from 'hooks/data/useCategory';
import { useUser } from 'hooks/data/useUser';
import { useAnalytics } from 'hooks/useAnalytics';

import MainCategoryTemplate from './MainCategoryTemplate';
import SubCategoryTemplate from './SubCategoryTemplate';

import type { ICategoryPageUrlParams } from 'types';

import styles from './CategoryTemplate.module.scss';

const CATEGORY_TYPES = {
  MAIN_CATEGORY: 'category',
  SUB_CATEGORY: 'subcategory'
};

interface Props {
  urlParams: ICategoryPageUrlParams
}

const CategoryTemplate = (props: Props) => {

  const { urlParams } = props;
  const { identifier } = urlParams;

  // Hooks

  const analytics = useAnalytics();

  const { setSlug } = useNavigationDispatchHelpers();

  const { data: user, isUserSettled } = useUser();
  const {
    data: category, isLoading, isError
  } = useCategory(urlParams);

  const { payload, type } = category || {};

  // Effects

  React.useEffect(() => {
    if (!isUserSettled || !payload) return;
    analytics.viewCategoryPage(user, payload);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserSettled, user?.id, payload?.name]);

  React.useEffect(() => {
    setSlug(identifier);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier]);

  // Error

  if (isError) {
    return <Error />;
  }

  // Loading

  if (isLoading) {
    return <Skeleton count={3} />;
  }

  // Empty

  if (!payload) return null;

  // Props

  const {
    breadcrumbs = [],
    image,
    name,
    items: products,
    total: count,
    filters,
    orderBy,
  } = payload;

  const isSubcategory = (type === CATEGORY_TYPES.SUB_CATEGORY);

  // Render

  return (
    <div className={styles.root}>

      {/* Breadcrumbs */}
      <Breadcrumbs
        className={styles.breadcrumbs}
        items={BreadcrumbsHelpers.getBreadcrumbsFromTrail(breadcrumbs)}
      />

      {/* Container */}
      <div className={styles.container}>

        {/* Side */}
        <ListingSidebar
          count={count}
          pageType={urlParams.pageType}
          //
          filters={filters}
          withFilters={isSubcategory}
          //
          withCategoryNavigation
        />

        {/* Main */}
        <div className={styles.main}>

          {/* Image */}
          {
            image && (
              <div className={styles.imageContainer}>
                <Image
                  className={styles.image}
                  skeletonClassName={styles.imageSkeleton}
                  src={image}
                  next={false}
                  alt="Category Banner"
                />
              </div>
            )
          }

          {/* Header */}
          <ListingHeader
            title={name}
            //
            items={products}
            //
            count={count}
            pageType={urlParams.pageType}
            //
            filters={isSubcategory ? filters : undefined}
            orderBy={isSubcategory ? orderBy : undefined}
            //
            titleDataTestId="category-title"
          />

          {/* Main Category Template */}
          {
            type === CATEGORY_TYPES.MAIN_CATEGORY && (
              <MainCategoryTemplate
                category={payload}
                urlParams={urlParams}
              />
            )
          }

          {/* Sub Category Template */}
          {
            type === CATEGORY_TYPES.SUB_CATEGORY && (
              <SubCategoryTemplate
                category={payload}
                urlParams={urlParams}
              />
            )
          }

        </div>
      </div>

    </div>
  );
};

// Export

export default CategoryTemplate;
